import {
  ANIMATION_KINDS,
  type AnimationFlags,
  type AnimationKind,
  RENDER_DISTANCE_MAX,
  RENDER_DISTANCE_MIN,
  RENDER_DISTANCE_STEP,
  SHADOW_RESOLUTIONS,
  type RenderSettings,
  type RenderSettingsState,
} from './RenderSettings';

const ANIMATION_LABELS: Record<AnimationKind, string> = {
  sequence: 'Sequence',
  parametric: 'Parametric',
  oscillator: 'Oscillator',
};

/**
 * Inventory-styled developer panel for live render toggles.
 * Instantiated only when the profile has isDev.
 */
export class DevPanel {
  private readonly root: HTMLElement;
  private open = false;

  constructor(
    root: HTMLElement,
    private readonly settings: RenderSettings,
    private readonly options: {
      canvas: HTMLElement;
      onOpenChange?(open: boolean): void;
    },
  ) {
    this.root = root;
    this.root.classList.add('dev-popup');
    this.render(this.settings.get());

    document.addEventListener('keydown', (event) => {
      if (event.code === 'F3' && !event.repeat && !isEditableTarget(event.target)) {
        event.preventDefault();
        this.toggle();
        return;
      }
      if (!this.open) return;
      if (event.code === 'Escape' && !event.repeat) {
        event.preventDefault();
        this.close(true);
      }
    });
  }

  get isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    if (this.open) this.close(true);
    else this.openPanel();
  }

  openPanel(): void {
    if (this.open) return;
    if (document.getElementById('inventory-popup')?.classList.contains('visible')) return;
    this.open = true;
    this.root.classList.add('visible');
    this.render(this.settings.get());
    document.exitPointerLock();
    this.options.onOpenChange?.(true);
  }

  close(relock: boolean): void {
    if (!this.open) return;
    this.open = false;
    this.root.classList.remove('visible');
    this.options.onOpenChange?.(false);
    if (relock) this.options.canvas.requestPointerLock();
  }

  private render(state: Readonly<RenderSettingsState>): void {
    this.root.innerHTML = `
      <section class="inventory-window dev-window">
        <header class="inventory-titlebar">
          <strong>Dev</strong>
          <em class="inventory-tip">Live render toggles</em>
          <button type="button" class="close-inventory close-dev" aria-label="Close developer panel">×</button>
        </header>
        <div class="dev-body">
          <section class="dev-section" aria-label="Effects">
            <h3 class="toolbar-section-label">Effects</h3>
            <div class="dev-toggle-list">
              <label class="dev-toggle">
                <input type="checkbox" data-setting="shadows" ${state.shadows ? 'checked' : ''}>
                <span>Shadows</span>
              </label>
              <label class="dev-toggle">
                <input type="checkbox" data-setting="glow" ${state.glow ? 'checked' : ''}>
                <span>Glow</span>
              </label>
            </div>
          </section>
          <section class="dev-section" aria-label="Animations">
            <h3 class="toolbar-section-label">Animations</h3>
            <div class="dev-toggle-list">
              ${ANIMATION_KINDS.map((kind) => `
                <label class="dev-toggle">
                  <input type="checkbox" data-anim="${kind}" ${state.animations[kind] ? 'checked' : ''}>
                  <span>${ANIMATION_LABELS[kind]}</span>
                </label>`).join('')}
            </div>
          </section>
          <section class="dev-section dev-section-wide" aria-label="Graphic params">
            <h3 class="toolbar-section-label">Graphic params</h3>
            <div class="dev-params-grid">
              <div class="dev-param">
                <div class="dev-param-head">
                  <span>Shadow resolution</span>
                  <strong class="dev-shadow-res-value">${state.shadowResolution}px</strong>
                </div>
                <div class="dev-slider-row">
                  <input
                    class="dev-shadow-res"
                    type="range"
                    min="0"
                    max="${SHADOW_RESOLUTIONS.length - 1}"
                    step="1"
                    value="${shadowResolutionIndex(state.shadowResolution)}"
                    aria-label="Shadow map resolution"
                  >
                </div>
              </div>
              <div class="dev-param">
                <div class="dev-param-head">
                  <span>Render distance</span>
                  <strong class="dev-distance-value">${state.renderDistance} blk</strong>
                </div>
                <div class="dev-slider-row">
                  <input
                    class="dev-distance"
                    type="range"
                    min="${RENDER_DISTANCE_MIN}"
                    max="${RENDER_DISTANCE_MAX}"
                    step="${RENDER_DISTANCE_STEP}"
                    value="${state.renderDistance}"
                    aria-label="Render distance in blocks"
                  >
                </div>
              </div>
            </div>
          </section>
        </div>
      </section>
    `;

    this.root.querySelector('.close-dev')?.addEventListener('click', () => {
      this.close(true);
    });

    for (const input of this.root.querySelectorAll<HTMLInputElement>('input[data-setting]')) {
      input.addEventListener('change', () => {
        const key = input.dataset.setting as 'shadows' | 'glow';
        this.settings.patch({ [key]: input.checked });
      });
    }

    for (const input of this.root.querySelectorAll<HTMLInputElement>('input[data-anim]')) {
      input.addEventListener('change', () => {
        const animations = { ...this.settings.get().animations } as AnimationFlags;
        const kind = input.dataset.anim as AnimationKind;
        animations[kind] = input.checked;
        this.settings.patch({ animations });
      });
    }

    const shadowRes = this.root.querySelector<HTMLInputElement>('.dev-shadow-res');
    const shadowResValue = this.root.querySelector<HTMLElement>('.dev-shadow-res-value');
    shadowRes?.addEventListener('input', () => {
      const resolution = SHADOW_RESOLUTIONS[Number(shadowRes.value)]
        ?? SHADOW_RESOLUTIONS[0]!;
      if (shadowResValue) shadowResValue.textContent = `${resolution}px`;
      this.settings.patch({ shadowResolution: resolution });
    });

    const distance = this.root.querySelector<HTMLInputElement>('.dev-distance');
    const distanceValue = this.root.querySelector<HTMLElement>('.dev-distance-value');
    distance?.addEventListener('input', () => {
      const value = Number(distance.value);
      if (distanceValue) distanceValue.textContent = `${value} blk`;
      this.settings.patch({ renderDistance: value });
    });
  }
}

function shadowResolutionIndex(resolution: number): number {
  const index = SHADOW_RESOLUTIONS.indexOf(
    resolution as (typeof SHADOW_RESOLUTIONS)[number],
  );
  return index >= 0 ? index : SHADOW_RESOLUTIONS.indexOf(4096);
}

function isEditableTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}
